/**
 * GREEETER.ai Journey Tracker Module
 *
 * Ported from the standalone tracker.js IIFE into an ES module class.
 * Initialised by the widget on mount; auto-tracks:
 *   · page views (initial + SPA + hash navigation)
 *   · section scroll visibility
 *   · smart click detection (buttons, links, toggles, downloads)
 *   · form submissions with field analysis
 *   · scroll depth milestones (25/50/75/100 %)
 *   · rage clicks (frustration detection)
 *   · toggle / checkbox / select changes
 *   · file download detection (.pdf, .doc, .zip, etc.)
 *   · UTM campaign parameter extraction
 *   · form field hesitation & abandonment
 *   · multi-step wizard step changes
 *   · section engagement time
 *   · tab exit / beforeunload
 *
 * All events are auto-labeled via DOM introspection — no customer
 * code changes required. The tracker reads element text, attributes,
 * and context to generate meaningful event names.
 *
 * Events are buffered and flushed in batches to
 *   POST /widget/journey/track/batch
 */
import { logger } from './logger.js';


// ─── Defaults ───
const DEFAULTS = {
    batchInterval: 5000,
    maxBatchSize: 50,
    trackSections: true,
    sectionSelector: 'section[id], [data-section], [id]:not(script):not(link):not(style)',
    sectionThreshold: 0.4,
    spa: 'auto',
    hashNav: true,
};

// ─── File extensions for download detection ───
const DOWNLOAD_EXTS = /\.(pdf|docx?|xlsx?|csv|pptx?|zip|rar|7z|tar|gz|mp3|mp4|avi|mov|dmg|exe|msi|apk|ipa)$/i;

// ─── UTM parameters to extract ───
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

export class JourneyTracker {
    /**
     * @param {object} opts
     * @param {string} opts.appId        — widget appId (sent as app_id in events)
     * @param {string} opts.apiUrl       — base URL of the Fastify backend
     * @param {string} [opts.visitorId]  — stable visitor ID from /widget/identify
     * @param {object} [opts.journey]    — optional overrides (see DEFAULTS)
     */
    constructor(opts) {
        this.appId = opts.appId;
        this.apiUrl = (opts.apiUrl || '').replace(/\/+$/, '');
        this.visitorId = opts.visitorId || this._getOrCreate('localStorage', '__gr_vid');
        this.sessionId = this._getOrCreate('sessionStorage', '__gr_sid');

        const j = opts.journey || {};
        this.trackSections = j.trackSections ?? DEFAULTS.trackSections;
        this.sectionSelector = j.sectionSelector || DEFAULTS.sectionSelector;
        this.sectionThreshold = j.sectionThreshold ?? DEFAULTS.sectionThreshold;
        this.spaMode = j.spa || DEFAULTS.spa;
        this.hashNav = j.hashNav ?? DEFAULTS.hashNav;

        // State
        this._buffer = [];
        this._lastTrackedPage = null;
        this._lastTrackedSection = null;
        this._sectionObserver = null;
        this._engagementObserver = null;
        this._pageEnterTime = Date.now();
        this._scrollMilestones = {};
        this._sectionTimers = {};
        this._clickLog = [];
        this._lastRageTime = 0;
        this._fieldState = {};
        this._lastDetectedStep = null;

        // Bound handlers (for removeEventListener)
        this._onBeforeUnload = this._handleBeforeUnload.bind(this);
        this._onVisibilityChange = this._handleVisibilityChange.bind(this);
        this._onPopState = this._handlePopState.bind(this);
        this._onHashChange = this._handleHashChange.bind(this);
        this._onDocClick = this._handleDocClick.bind(this);
        this._onFormSubmit = this._handleFormSubmit.bind(this);
        this._onScroll = this._handleScroll.bind(this);
        this._onRageClick = this._handleRageClick.bind(this);
        this._onFocusIn = this._handleFocusIn.bind(this);
        this._onInput = this._handleInput.bind(this);
        this._onFocusOut = this._handleFocusOut.bind(this);
        this._onChange = this._handleChange.bind(this);
        this._onMediaPlay = this._handleMediaPlay.bind(this);

        // Monkey-patched originals
        this._origPushState = null;
        this._origReplaceState = null;
        this._stepObserver = null;
        this._flushInterval = null;
        this._scrollTimeout = null;

        this._destroyed = false;

        // Flush gate: if set, the first flush waits for identification to finish
        // so that all events use the stable consumer visitorId.
        this._identifyPromise = null;
    }

    // ═══════════════════════════════════════════════
    //  PUBLIC API
    // ═══════════════════════════════════════════════

    /** Start tracking. Call once when the widget mounts. */
    start() {
        if (this._destroyed) return;

        // Extract UTM params from the landing URL
        this._utmParams = this._extractUTMs();

        // Initial page view
        this._lastTrackedPage = this._getCurrentPage();
        this._track('page_view', {
            navigation_method: 'initial',
            ...this._utmParams,
        });

        // SPA nav
        if (this._isSPA() || this.spaMode === 'auto') {
            this._initSPATracking();
        }

        // Hash nav
        if (this.hashNav) {
            window.addEventListener('hashchange', this._onHashChange);
        }

        // Section visibility
        if (this.trackSections && 'IntersectionObserver' in window) {
            this._readyThen(() => this._observeSections());
        }

        // Clicks, forms, scroll, toggles, media
        document.addEventListener('click', this._onDocClick, true);
        document.addEventListener('submit', this._onFormSubmit, true);
        document.addEventListener('change', this._onChange, true);
        window.addEventListener('scroll', this._onScroll, { passive: true });

        // Video/audio play tracking
        this._readyThen(() => {
            document.querySelectorAll('video, audio').forEach((el) => {
                el.addEventListener('play', this._onMediaPlay);
            });
        });

        // Advanced tracking
        try {
            if (this.trackSections) this._initEngagementTracking();
            this._initRageClickTracking();
            this._initFormFieldTracking();
            this._initStepDetection();
        } catch (e) {
            logger.warn('[Tracker] Advanced tracking init error:', e.message);
        }

        // Exit + flush
        window.addEventListener('beforeunload', this._onBeforeUnload);
        document.addEventListener('visibilitychange', this._onVisibilityChange);
        this._flushInterval = setInterval(() => this._flush(), DEFAULTS.batchInterval);

        logger.info(
            `[Tracker] Initialized | appId: ${this.appId} | SPA: ${this._isSPA() ? 'yes' : 'no'} | sections: ${this.trackSections ? 'on' : 'off'}`
        );
    }

    /** Stop tracking and clean up all listeners/observers/timers. */
    destroy() {
        if (this._destroyed) return;
        this._destroyed = true;

        // Flush remaining
        this._flush();

        // Clear timer
        if (this._flushInterval) clearInterval(this._flushInterval);

        // Remove listeners
        window.removeEventListener('beforeunload', this._onBeforeUnload);
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        window.removeEventListener('hashchange', this._onHashChange);
        window.removeEventListener('popstate', this._onPopState);
        document.removeEventListener('click', this._onDocClick, true);
        document.removeEventListener('click', this._onRageClick, true);
        document.removeEventListener('submit', this._onFormSubmit, true);
        document.removeEventListener('change', this._onChange, true);
        window.removeEventListener('scroll', this._onScroll);
        document.removeEventListener('focusin', this._onFocusIn, true);
        document.removeEventListener('input', this._onInput, true);
        document.removeEventListener('focusout', this._onFocusOut, true);

        // Restore history
        if (this._origPushState) history.pushState = this._origPushState;
        if (this._origReplaceState) history.replaceState = this._origReplaceState;

        // Disconnect observers
        if (this._sectionObserver) this._sectionObserver.disconnect();
        if (this._engagementObserver) this._engagementObserver.disconnect();
        if (this._stepObserver) this._stepObserver.disconnect();

        if (this._scrollTimeout) clearTimeout(this._scrollTimeout);

        this._buffer = [];
    }

    /** Allow external code to push custom events. */
    trackCustom(eventType, metadata) {
        this._track(eventType || 'custom', metadata || {});
    }

    /** Force re-scan of sections (after dynamic DOM changes). */
    rescan() {
        if (this.trackSections && this._sectionObserver) {
            this._observeSections();
        }
    }

    // ═══════════════════════════════════════════════
    //  CORE: Track Event
    // ═══════════════════════════════════════════════

    _track(eventType, extra, pageOverride) {
        if (this._destroyed) return;

        const page = pageOverride || this._getCurrentPage();

        this._buffer.push({
            appId: this.appId,
            session_id: this.sessionId,
            visitor_id: this.visitorId,
            page,
            page_title: document.title,
            referrer: document.referrer,
            event_type: eventType,
            metadata: extra || {},
            device: this._getDevice(),
            browser: this._getBrowser(),
            source: this._getSource(),
            timestamp: new Date().toISOString(),
        });

        if (this._buffer.length >= DEFAULTS.maxBatchSize) {
            this._flush();
        }
    }

    // ═══════════════════════════════════════════════
    //  FLUSH
    // ═══════════════════════════════════════════════

    async _flush() {
        if (this._buffer.length === 0) return;

        // Gate: wait for consumer identification before first flush
        if (this._identifyPromise) {
            try {
                await this._identifyPromise;
            } catch { /* identification failed — flush with fallback ID */ }
            this._identifyPromise = null; // only wait once
        }

        const events = this._buffer.splice(0, DEFAULTS.maxBatchSize);
        const payload = JSON.stringify({ events });
        const url = this.apiUrl + '/widget/journey/track/batch';

        if (navigator.sendBeacon) {
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon(url, blob);
        } else {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', url, true);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.send(payload);
        }
    }

    // ═══════════════════════════════════════════════
    //  SPA: History API tracking
    // ═══════════════════════════════════════════════

    _initSPATracking() {
        this._origPushState = history.pushState;
        this._origReplaceState = history.replaceState;
        const self = this;

        history.pushState = function () {
            self._origPushState.apply(this, arguments);
            self._onRouteChange('pushState');
        };
        history.replaceState = function () {
            self._origReplaceState.apply(this, arguments);
            self._onRouteChange('replaceState');
        };

        window.addEventListener('popstate', this._onPopState);
    }

    _handlePopState() {
        this._onRouteChange('popstate');
    }

    _onRouteChange(method) {
        const currentPage = this._getCurrentPage();
        if (currentPage === this._lastTrackedPage) return;

        // Exit from previous page
        const timeOnPage = Math.round((Date.now() - this._pageEnterTime) / 1000);
        if (this._lastTrackedPage) {
            this._track('exit', {
                time_on_page_sec: timeOnPage,
                navigation_method: method,
                navigated_to: currentPage,
            }, this._lastTrackedPage);
        }

        // New page view
        this._lastTrackedPage = currentPage;
        this._pageEnterTime = Date.now();
        this._track('page_view', { navigation_method: method });
        this._scrollMilestones = {};

        // Re-observe sections
        if (this.trackSections) {
            setTimeout(() => this._observeSections(), 500);
        }
    }

    // ─── Hash nav ───

    _handleHashChange() {
        const currentPage = this._getCurrentPage();
        if (currentPage === this._lastTrackedPage) return;

        const timeOnPage = Math.round((Date.now() - this._pageEnterTime) / 1000);
        if (this._lastTrackedPage) {
            this._track('exit', {
                time_on_page_sec: timeOnPage,
                navigation_method: 'hashchange',
                navigated_to: currentPage,
            }, this._lastTrackedPage);
        }

        this._lastTrackedPage = currentPage;
        this._pageEnterTime = Date.now();
        this._track('page_view', { navigation_method: 'hashchange' });
    }

    // ═══════════════════════════════════════════════
    //  SECTIONS: IntersectionObserver
    // ═══════════════════════════════════════════════

    _observeSections() {
        if (this._sectionObserver) this._sectionObserver.disconnect();

        const sections = document.querySelectorAll(this.sectionSelector);
        const valid = Array.from(sections).filter((el) => el.offsetHeight > 100 && el.id);
        if (valid.length === 0) return;

        this._sectionObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) continue;
                    const sectionId = entry.target.id || entry.target.getAttribute('data-section');
                    if (!sectionId || sectionId === this._lastTrackedSection) continue;

                    this._lastTrackedSection = sectionId;
                    this._track('section_view', {
                        section_id: sectionId,
                        section_title: this._getSectionTitle(entry.target),
                        scroll_depth: this._getScrollDepth(),
                        viewport_pct: Math.round(entry.intersectionRatio * 100),
                    });
                }
            },
            { threshold: this.sectionThreshold, rootMargin: '0px' }
        );

        for (const s of valid) this._sectionObserver.observe(s);
    }

    // ═══════════════════════════════════════════════
    //  SMART CLICK DETECTION
    //  Reads DOM context to auto-label every click
    // ═══════════════════════════════════════════════

    _handleDocClick(e) {
        const target = e.target;
        if (!target || !target.closest) return;

        // Find the nearest meaningful clickable element
        const clickable = target.closest('a, button, [role="button"], input[type="submit"], input[type="button"], summary, [onclick], .btn, .cta');

        // ── 1. Link clicks (anchor/download/external/CTA) ──
        const link = target.closest('a');
        if (link) {
            const href = link.getAttribute('href') || '';
            const label = this._getSmartLabel(link);

            // Download detection
            if (DOWNLOAD_EXTS.test(href) || link.hasAttribute('download')) {
                const filename = href.split('/').pop().split('?')[0];
                const ext = filename.split('.').pop().toUpperCase();
                this._track('click', {
                    click_type: 'download',
                    event_name: `Download ${ext}`,
                    file_name: filename,
                    target_url: href,
                    link_text: label,
                });
                return;
            }

            // External link
            if (href.startsWith('http') && !href.includes(window.location.hostname)) {
                this._track('click', {
                    click_type: 'external_link',
                    event_name: `External Link: ${label || new URL(href).hostname}`,
                    target_url: href,
                    link_text: label,
                });
                return;
            }

            // Anchor/hash link
            if (href.startsWith('#') && href.length > 1) {
                this._track('click', {
                    click_type: 'anchor_link',
                    event_name: `Jump to ${this._formatId(href.substring(1))}`,
                    target_section: href.substring(1),
                    link_text: label,
                });
                return;
            }

            // Internal navigation link
            if (href && href !== '#' && !href.startsWith('javascript')) {
                this._track('click', {
                    click_type: 'navigation',
                    event_name: label || `Navigate to ${href}`,
                    target_url: href,
                    link_text: label,
                    link_id: link.id || '',
                });
                return;
            }
        }

        // ── 2. Button clicks ──
        if (clickable && !link) {
            const label = this._getSmartLabel(clickable);
            if (!label) return; // skip unlabeled invisible elements

            this._track('click', {
                click_type: 'button',
                event_name: label,
                target_tag: clickable.tagName.toLowerCase(),
                target_id: clickable.id || '',
                target_classes: (clickable.className || '').toString().substring(0, 100),
            });
        }
    }

    // ═══════════════════════════════════════════════
    //  TOGGLE / SELECT CHANGE DETECTION
    // ═══════════════════════════════════════════════

    _handleChange(e) {
        const el = e.target;
        if (!el || !el.tagName) return;
        const tag = el.tagName.toLowerCase();

        // Checkbox / radio toggle
        if (tag === 'input' && (el.type === 'checkbox' || el.type === 'radio')) {
            const label = this._getFieldLabel(el);
            const state = el.checked ? 'On' : 'Off';
            this._track('click', {
                click_type: 'toggle',
                event_name: `Toggle ${label}`,
                toggle_state: state,
                field_name: el.name || el.id || '',
                field_value: el.value || state,
            });
            return;
        }

        // Select dropdown change
        if (tag === 'select') {
            const label = this._getFieldLabel(el);
            const selectedText = el.options[el.selectedIndex]?.text || el.value;
            this._track('click', {
                click_type: 'select_change',
                event_name: `Select ${label}: ${selectedText}`,
                field_name: el.name || el.id || '',
                field_value: el.value,
                selected_text: selectedText,
            });
        }
    }

    // ═══════════════════════════════════════════════
    //  MEDIA PLAY DETECTION
    // ═══════════════════════════════════════════════

    _handleMediaPlay(e) {
        const el = e.target;
        const isVideo = el.tagName.toLowerCase() === 'video';
        const src = el.currentSrc || el.src || '';
        const filename = src.split('/').pop().split('?')[0] || 'unknown';

        this._track('custom', {
            event_name: isVideo ? `Play Video: ${filename}` : `Play Audio: ${filename}`,
            media_type: isVideo ? 'video' : 'audio',
            media_src: src.substring(0, 200),
            media_duration: el.duration ? Math.round(el.duration) : null,
        });
    }

    // ═══════════════════════════════════════════════
    //  FORMS
    // ═══════════════════════════════════════════════

    _handleFormSubmit(e) {
        const form = e.target;
        if (!form || form.tagName !== 'FORM') return;

        // Smart form label: heading inside form, or aria-label, or form name/id
        const heading = form.querySelector('h1, h2, h3, h4, h5, legend');
        const formLabel = heading
            ? (heading.textContent || '').trim()
            : form.getAttribute('aria-label')
            || form.getAttribute('name')
            || form.id
            || 'Form';

        // Collect field names for context
        const fieldNames = Array.from(form.elements)
            .filter((el) => el.name && el.type !== 'hidden' && el.type !== 'submit')
            .map((el) => el.name)
            .slice(0, 10);

        this._track('form_submit', {
            event_name: `Submit: ${formLabel}`,
            form_id: form.id || '',
            form_action: form.action || '',
            form_name: form.getAttribute('name') || '',
            form_label: formLabel,
            fields: fieldNames,
            fields_count: form.elements ? form.elements.length : 0,
        });
    }

    // ═══════════════════════════════════════════════
    //  SCROLL DEPTH
    // ═══════════════════════════════════════════════

    _handleScroll() {
        if (this._scrollTimeout) return;
        this._scrollTimeout = setTimeout(() => {
            this._scrollTimeout = null;
            this._checkScrollMilestones();
        }, 500);
    }

    _checkScrollMilestones() {
        const depth = this._getScrollDepth();
        const page = this._getCurrentPage();
        const milestones = [25, 50, 75, 100];

        for (const m of milestones) {
            const key = page + '_' + m;
            if (depth >= m && !this._scrollMilestones[key]) {
                this._scrollMilestones[key] = true;

                // Try to identify the visible section at this depth
                const visibleSection = this._getVisibleSection();

                this._track('scroll', {
                    event_name: `Scroll Depth ${m}%`,
                    scroll_milestone: m,
                    exact_depth: depth,
                    visible_section: visibleSection,
                    time_to_milestone_sec: Math.round((Date.now() - this._pageEnterTime) / 1000),
                });
            }
        }
    }

    // ═══════════════════════════════════════════════
    //  ENGAGEMENT
    // ═══════════════════════════════════════════════

    _initEngagementTracking() {
        if (!('IntersectionObserver' in window)) return;

        this._engagementObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const id = entry.target.id || entry.target.getAttribute('data-section');
                    if (!id) continue;

                    if (entry.isIntersecting) {
                        if (!this._sectionTimers[id]) {
                            this._sectionTimers[id] = { startTime: 0, totalTime: 0, visible: false };
                        }
                        this._sectionTimers[id].startTime = Date.now();
                        this._sectionTimers[id].visible = true;
                    } else {
                        const timer = this._sectionTimers[id];
                        if (timer && timer.visible) {
                            const elapsed = Math.round((Date.now() - timer.startTime) / 1000);
                            timer.totalTime += elapsed;
                            timer.visible = false;

                            if (elapsed >= 2) {
                                this._track('custom', {
                                    event_name: 'section_engagement',
                                    section_id: id,
                                    section_title: this._getSectionTitle(entry.target),
                                    engagement_sec: elapsed,
                                    total_engagement_sec: timer.totalTime,
                                });
                            }
                        }
                    }
                }
            },
            { threshold: 0.3 }
        );

        this._readyThen(() => {
            const sections = document.querySelectorAll(this.sectionSelector);
            for (const el of sections) {
                if (el.offsetHeight > 100 && el.id) {
                    this._engagementObserver.observe(el);
                }
            }
        });
    }

    // ═══════════════════════════════════════════════
    //  RAGE CLICKS
    // ═══════════════════════════════════════════════

    _initRageClickTracking() {
        document.addEventListener('click', this._onRageClick, true);
    }

    _handleRageClick(e) {
        const now = Date.now();
        this._clickLog.push({ time: now, x: e.clientX, y: e.clientY });

        this._clickLog = this._clickLog.filter((c) => now - c.time <= 800);

        if (this._clickLog.length >= 3) {
            const first = this._clickLog[0];
            const allNearby = this._clickLog.every((c) => {
                const dx = c.x - first.x;
                const dy = c.y - first.y;
                return Math.sqrt(dx * dx + dy * dy) <= 50;
            });

            if (allNearby && now - this._lastRageTime > 2000) {
                this._lastRageTime = now;
                const target = e.target;
                const meaningful = target.closest('a, button, [role="button"], input, select, .btn, .cta');

                this._track('click', {
                    click_type: 'rage_click',
                    click_count: this._clickLog.length,
                    target_tag: target.tagName.toLowerCase(),
                    target_id: target.id || '',
                    target_text: (target.textContent || '').trim().substring(0, 50),
                    is_interactive: !!meaningful,
                    coordinates: { x: e.clientX, y: e.clientY },
                });

                this._clickLog = [];
            }
        }
    }

    // ═══════════════════════════════════════════════
    //  FORM FIELDS (hesitation)
    // ═══════════════════════════════════════════════

    _initFormFieldTracking() {
        document.addEventListener('focusin', this._onFocusIn, true);
        document.addEventListener('input', this._onInput, true);
        document.addEventListener('focusout', this._onFocusOut, true);
    }

    _handleFocusIn(e) {
        const el = e.target;
        if (!this._isFormField(el)) return;

        const key = this._getFieldKey(el);
        this._fieldState[key] = {
            focusTime: Date.now(),
            firstKeystrokeTime: null,
            hasTyped: false,
            fieldType: el.type || el.tagName.toLowerCase(),
            fieldName: el.name || el.id || el.placeholder || '',
            formId: el.form ? (el.form.id || el.form.name || '') : '',
        };
    }

    _handleInput(e) {
        const el = e.target;
        if (!this._isFormField(el)) return;

        const key = this._getFieldKey(el);
        if (this._fieldState[key] && !this._fieldState[key].hasTyped) {
            this._fieldState[key].firstKeystrokeTime = Date.now();
            this._fieldState[key].hasTyped = true;
        }
    }

    _handleFocusOut(e) {
        const el = e.target;
        if (!this._isFormField(el)) return;

        const key = this._getFieldKey(el);
        const state = this._fieldState[key];
        if (!state) return;

        const totalFocusTime = Math.round((Date.now() - state.focusTime) / 1000);
        const hesitationMs = state.firstKeystrokeTime ? state.firstKeystrokeTime - state.focusTime : null;

        if (totalFocusTime < 1 && !hesitationMs) return;

        this._track('custom', {
            event_name: 'form_field_interaction',
            field_name: state.fieldName,
            field_type: state.fieldType,
            form_id: state.formId,
            focus_duration_sec: totalFocusTime,
            hesitation_ms: hesitationMs,
            did_type: state.hasTyped,
            field_abandoned: !state.hasTyped && totalFocusTime >= 3,
        });

        delete this._fieldState[key];
    }

    // ═══════════════════════════════════════════════
    //  STEP DETECTION (wizard forms)
    // ═══════════════════════════════════════════════

    _initStepDetection() {
        if (!('MutationObserver' in window)) return;

        this._stepObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (!mutation.addedNodes || mutation.addedNodes.length === 0) continue;

                for (const node of mutation.addedNodes) {
                    if (!node.querySelector && !node.getAttribute) continue;

                    let stepEl = null;
                    if (node.getAttribute && node.getAttribute('data-track-step')) {
                        stepEl = node;
                    }
                    if (!stepEl && node.querySelector) {
                        stepEl = node.querySelector('[data-track-step]');
                    }

                    if (stepEl) {
                        const stepNum = stepEl.getAttribute('data-track-step');
                        const stepTitle = stepEl.getAttribute('data-step-title') || stepNum;

                        if (stepNum === this._lastDetectedStep) continue;

                        let direction = 'initial';
                        if (this._lastDetectedStep !== null) {
                            direction = parseInt(stepNum) > parseInt(this._lastDetectedStep) ? 'forward' : 'backward';
                        }

                        const prevStep = this._lastDetectedStep;
                        this._lastDetectedStep = stepNum;

                        this._track('custom', {
                            event_name: 'step_change',
                            step: parseInt(stepNum),
                            step_title: stepTitle,
                            previous_step: prevStep ? parseInt(prevStep) : null,
                            direction,
                        });
                    }
                }
            }
        });

        this._readyThen(() => {
            if (document.body) {
                this._stepObserver.observe(document.body, { childList: true, subtree: true });
            }
        });
    }

    // ═══════════════════════════════════════════════
    //  EXIT / VISIBILITY
    // ═══════════════════════════════════════════════

    _handleBeforeUnload() {
        const timeOnPage = Math.round((Date.now() - this._pageEnterTime) / 1000);
        this._track('exit', {
            time_on_page_sec: timeOnPage,
            final_scroll_depth: this._getScrollDepth(),
        });
        this._flush();
    }

    _handleVisibilityChange() {
        if (document.visibilityState === 'hidden') {
            this._flush();
        }
    }

    // ═══════════════════════════════════════════════
    //  HELPERS
    // ═══════════════════════════════════════════════

    /**
     * Extracts a meaningful label from any DOM element by checking
     * multiple sources in priority order. No customer code changes needed.
     */
    _getSmartLabel(el) {
        if (!el) return '';

        // 1. Explicit data-track attribute (if customer added one)
        const dataTrack = el.getAttribute('data-track');
        if (dataTrack) return dataTrack;

        // 2. aria-label (accessibility attribute)
        const ariaLabel = el.getAttribute('aria-label');
        if (ariaLabel) return ariaLabel;

        // 3. title attribute
        const title = el.getAttribute('title');
        if (title) return title;

        // 4. alt text (for images inside buttons/links)
        const img = el.querySelector('img');
        if (img && img.alt) return img.alt;

        // 5. Input value (for submit buttons)
        if (el.tagName === 'INPUT' && (el.type === 'submit' || el.type === 'button')) {
            return el.value || '';
        }

        // 6. Direct text content (skip if too long or has child elements with text)
        const text = (el.textContent || '').trim();
        if (text && text.length <= 80 && text.length > 0) {
            // Clean up whitespace from nested elements
            return text.replace(/\s+/g, ' ');
        }

        // 7. First meaningful child text
        const firstText = el.querySelector('span, strong, em, b, p, h1, h2, h3, h4, div');
        if (firstText) {
            const childText = (firstText.textContent || '').trim();
            if (childText && childText.length <= 80) return childText;
        }

        // 8. ID or class-based fallback
        if (el.id) return this._formatId(el.id);

        return '';
    }

    /**
     * Gets a human-readable label for a form field by checking
     * associated <label>, placeholder, aria-label, or name.
     */
    _getFieldLabel(el) {
        // Check for associated <label>
        if (el.id) {
            const label = document.querySelector(`label[for="${el.id}"]`);
            if (label) return (label.textContent || '').trim();
        }

        // Check parent label
        const parentLabel = el.closest('label');
        if (parentLabel) {
            // Get label text excluding the input's own text
            const clone = parentLabel.cloneNode(true);
            const inputs = clone.querySelectorAll('input, select, textarea');
            inputs.forEach((i) => i.remove());
            const text = (clone.textContent || '').trim();
            if (text) return text;
        }

        // aria-label
        const ariaLabel = el.getAttribute('aria-label');
        if (ariaLabel) return ariaLabel;

        // placeholder
        if (el.placeholder) return el.placeholder;

        // name attribute formatted
        if (el.name) return this._formatId(el.name);

        return el.id ? this._formatId(el.id) : 'Unknown field';
    }

    /**
     * Returns the title of the section currently visible in the viewport.
     */
    _getVisibleSection() {
        const sections = document.querySelectorAll(this.sectionSelector);
        const viewportMiddle = window.scrollY + window.innerHeight / 2;

        for (const section of sections) {
            const rect = section.getBoundingClientRect();
            const top = rect.top + window.scrollY;
            const bottom = top + rect.height;

            if (viewportMiddle >= top && viewportMiddle <= bottom) {
                return this._getSectionTitle(section);
            }
        }
        return null;
    }

    /**
     * Extracts UTM parameters from the current URL.
     */
    _extractUTMs() {
        const params = new URLSearchParams(window.location.search);
        const utms = {};
        for (const key of UTM_PARAMS) {
            const val = params.get(key);
            if (val) utms[key] = val;
        }
        return Object.keys(utms).length > 0 ? utms : null;
    }

    _getCurrentPage() {
        const path = window.location.pathname;
        const hash = window.location.hash;
        return (hash && this.hashNav) ? path + hash : path;
    }

    _getScrollDepth() {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) return 100;
        return Math.round((scrollTop / docHeight) * 100);
    }

    _getSectionTitle(el) {
        return (
            el.getAttribute('data-section-title') ||
            el.getAttribute('data-section') ||
            el.getAttribute('aria-label') ||
            this._formatId(el.id) ||
            document.title
        );
    }

    _formatId(id) {
        if (!id) return '';
        return id
            .replace(/[-_]/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/^./, (c) => c.toUpperCase());
    }

    _getDevice() {
        const w = window.innerWidth;
        if (w <= 768) return 'mobile';
        if (w <= 1024) return 'tablet';
        return 'desktop';
    }

    _getBrowser() {
        const ua = navigator.userAgent;
        if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) return 'chrome';
        if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) return 'safari';
        if (ua.indexOf('Firefox') > -1) return 'firefox';
        if (ua.indexOf('Edg') > -1) return 'edge';
        return 'other';
    }

    _getSource() {
        const params = new URLSearchParams(window.location.search);
        return params.get('utm_source') || params.get('ref') || this._extractDomain(document.referrer) || 'direct';
    }

    _extractDomain(url) {
        if (!url) return '';
        try {
            const hostname = new URL(url).hostname;
            if (hostname === window.location.hostname) return '';
            return hostname;
        } catch { return ''; }
    }

    _isSPA() {
        if (this.spaMode === 'true') return true;
        if (this.spaMode === 'false') return false;
        return !!(
            document.querySelector('[data-reactroot], #__next, #__nuxt, #app[data-v-app], [ng-version]') ||
            window.__NEXT_DATA__ || window.__NUXT__ || window.Angular || window.__VUE__
        );
    }

    _isFormField(el) {
        if (!el || !el.tagName) return false;
        const tag = el.tagName.toLowerCase();
        return tag === 'input' || tag === 'textarea' || tag === 'select';
    }

    _getFieldKey(el) {
        return (el.form ? el.form.id || 'form' : 'noform') + '_' + (el.name || el.id || el.type || 'field');
    }

    _getOrCreate(storageType, key) {
        try {
            const storage = window[storageType];
            let val = storage.getItem(key);
            if (val) return val;
            val = this._generateId();
            storage.setItem(key, val);
            return val;
        } catch {
            return this._generateId();
        }
    }

    _generateId() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    _readyThen(fn) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', fn);
        } else {
            fn();
        }
    }
}
